type Checkboxes = Record<string, boolean>;

interface DependencyRule {
  attr: string;
  lockedWhen: (state: Checkboxes, officeAdminChecked: boolean) => boolean;
  lockedValue?: boolean;
}

const DEPENDENCY_RULES: DependencyRule[] = [
  { attr: 'can_create_properties', lockedWhen: s => !s.can_access_property_edit_form },
  { attr: 'can_send_emails_for_other_users_properties', lockedWhen: s => !s.can_send_emails },
  { attr: 'can_send_emails_with_company_credentials', lockedWhen: s => !s.can_send_emails },
  { attr: 'can_send_emails_with_other_users_credentials', lockedWhen: s => !s.can_send_emails },
  { attr: 'can_edit_other_users_documents', lockedWhen: s => !s.can_view_other_users_documents },
  { attr: 'elite_backoffice_admin', lockedWhen: s => !s.backoffice_user },
  { attr: 'can_approve_vouchers', lockedWhen: s => !s.backoffice_user },
  {
    attr: 'can_edit_other_users_vouchers',
    lockedWhen: (s, officeAdminChecked) => !s.backoffice_user || s.elite_backoffice_admin || officeAdminChecked,
  },
  {
    attr: 'can_edit_other_users_deals',
    lockedWhen: s => s.can_edit_other_users_vouchers,
    lockedValue: true,
  },
];

const RULES_BY_ATTR = new Map(DEPENDENCY_RULES.map(rule => [rule.attr, rule]));

export function dependencyDisabled(attr: string, checkboxes: Checkboxes, officeAdminChecked: boolean): boolean {
  const rule = RULES_BY_ATTR.get(attr);
  return rule ? rule.lockedWhen(checkboxes, officeAdminChecked) : false;
}

export function applyDependencies(
  checkboxes: Checkboxes,
  officeAdminChecked: boolean,
  previous?: Checkboxes,
): Checkboxes {
  const next = { ...checkboxes };

  DEPENDENCY_RULES.forEach((rule) => {
    if (rule.attr in next && rule.lockedWhen(next, officeAdminChecked)) {
      next[rule.attr] = rule.lockedValue ?? false;
    }
  });

  // Releasing voucher access also releases the deal access it had forced on.
  // This is a transition (not a steady state), so it needs the prior state.
  if (previous?.can_edit_other_users_vouchers && !next.can_edit_other_users_vouchers
      && 'can_edit_other_users_deals' in next) {
    next.can_edit_other_users_deals = false;
  }

  return next;
}
